import type { Identifier, RaRecord } from "ra-core";
import type { ComponentType } from "react";

import type {
  COMPANY_CREATED,
  CONTACT_CREATED,
  CONTACT_NOTE_CREATED,
  DEAL_CREATED,
  DEAL_NOTE_CREATED,
} from "./consts";

export type SignUpData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
};

export type SalesFormData = {
  avatar?: string;
  email: string;
  secondary_emails?: string[];
  password?: string;
  first_name: string;
  last_name: string;
  administrator: boolean;
  disabled: boolean;
};

export type Sale = {
  first_name: string;
  last_name: string;
  administrator: boolean;
  avatar?: RAFile;
  disabled?: boolean;
  user_id: string;

  /**
   * This is a copy of the user's email, to make it easier to handle by react admin
   * DO NOT UPDATE this field directly, it should be updated by the backend
   */
  email: string;

  secondary_emails?: string[];

  /**
   * This is used by the fake rest provider to store the password
   * DO NOT USE this field in your code besides the fake rest provider
   * @deprecated
   */
  password?: string;
} & Pick<RaRecord, "id">;

export type Company = {
  name: string;
  logo: RAFile;
  sector: string;
  size: 1 | 10 | 50 | 250 | 500;
  linkedin_url: string;
  website: string;
  phone_number: string;
  address: string;
  zipcode: string;
  city: string;
  state_abbr: string;
  nb_contacts?: number;
  nb_deals?: number;
  sales_id: Identifier;
  created_at: string;
  description: string;
  revenue: string;
  tax_identifier: string;
} & Pick<RaRecord, "id">;

export type EmailAndType = {
  email: string;
  type: "Work" | "Home" | "Other";
};

export type PhoneNumberAndType = {
  number: string;
  type: "Work" | "Home" | "Other";
};

export type SequenceStep = {
  step: number;
  channel: "linkedin" | "email";
  day: number;
  title: string;
  message: string;
  status: "pending" | "completed" | "skipped";
  task_id?: number | null;
};

export type JobChangeAlert = {
  id: number;
  contact_id: number;
  old_company: string;
  new_company: string;
  old_title?: string | null;
  new_title?: string | null;
  detected_at: string;
  ai_reengagement_hook?: string | null;
  acknowledged: boolean;
  contact_name?: string;
  linkedin_url?: string;
};

export type Contact = {
  first_name: string;
  last_name: string;
  title: string;
  company_id: Identifier;
  email_jsonb: EmailAndType[];
  avatar?: RAFile;
  first_seen: string;
  last_seen: string;
  has_newsletter: boolean;
  tags: number[];
  gender: string;
  sales_id?: Identifier;
  status: string;
  background: string;
  phone_jsonb: PhoneNumberAndType[];
  nb_tasks?: number;
  company_name?: string;
  headline?: string | null;
  ai_icebreaker?: string | null;
  detected_company?: string | null;
  job_change_flagged?: boolean | null;
  raw_linkedin_json?: Record<string, unknown> | null;
  lead_score?: number | null;
  outreach_stage?: string | null;
  ai_dm_variants?: Array<{
    label: string;
    angle: string;
    message: string;
  }> | null;
  recent_post_summary?: string | null;
  next_followup_at?: string | null;
  last_enriched_at?: string | null;
  ai_sequence?: SequenceStep[] | null;
  is_champion?: boolean | null;
  group_entity?: "five_lens" | "learn_smartly" | "ennea_world" | string | null;
} & Pick<RaRecord, "id">;

export type ContactNote = {
  contact_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  status: string;
  attachments?: AttachmentNote[];
} & Pick<RaRecord, "id">;

export type Deal = {
  name: string;
  company_id: Identifier;
  contact_ids: Identifier[];
  categories: string[];
  stage: string;
  description: string;
  amount: number;
  currency?: string;
  group_entity?: "five_lens" | "learn_smartly" | "ennea_world" | string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  expected_closing_date: string;
  sales_id: Identifier;
  index: number;
  nb_notes?: number;
} & Pick<RaRecord, "id">;

export type DealNote = {
  deal_id: Identifier;
  type: string;
  text: string;
  date: string;
  sales_id: Identifier;
  attachments?: AttachmentNote[];
} & Pick<RaRecord, "id">;

export type Tag = {
  name: string;
  color: string;
} & Pick<RaRecord, "id">;

export type Task = {
  contact_id: Identifier;
  type: string;
  text: string;
  due_date: string;
  done_date?: string;
  sales_id: Identifier;
} & Pick<RaRecord, "id">;

export type Activity =
  | ({
      type: typeof CONTACT_CREATED;
      activityDate: string;
      contact_id: Identifier;
    } & Pick<RaRecord, "id">)
  | ({
      type: typeof CONTACT_NOTE_CREATED;
      activityDate: string;
      contact_id: Identifier;
      contactNote: ContactNote;
    } & Pick<RaRecord, "id">)
  | ({
      type: typeof DEAL_CREATED;
      activityDate: string;
      deal: Deal;
    } & Pick<RaRecord, "id">)
  | ({
      type: typeof DEAL_NOTE_CREATED;
      activityDate: string;
      deal: Deal;
      dealNote: DealNote;
    } & Pick<RaRecord, "id">);

export type RAFile = {
  rawFile?: File;
  src: string;
  title?: string;
  path?: string;
};

export type AttachmentNote = {
  rawFile?: File;
  src: string;
  title: string;
  path?: string;
  type: string;
};

export type LabeledValue = {
  value: string;
  label: string;
};

export type DealStage = LabeledValue;

export type NoteStatus = {
  value: string;
  label: string;
  color: string;
};

export type SearchResourceName = "contacts" | "companies" | "deals";

export type SearchResult = {
  id: Identifier;
  content: string;
  resource: SearchResourceName;
};

export interface PageComponent {
  path: string;
  component: ComponentType;
}

export type GroupEntityId = "five_lens" | "learn_smartly" | "ennea_world";

export interface GroupEntity {
  id: GroupEntityId;
  name: string;
  domain: string;
  badge: string;
  tagline: string;
  coreOffering: string;
  targetBuyer: string;
}

export const GROUP_ENTITIES: Record<GroupEntityId, GroupEntity> = {
  five_lens: {
    id: "five_lens",
    name: "Five Lens Enneagram",
    domain: "fivelensenneagram.com",
    badge: "5 Lens",
    tagline: "Deep psychological Enneagram assessments & executive leadership profiling",
    coreOffering: "World-class Five Lens diagnostic reports, coach accreditation, and emotional agility frameworks for leadership teams",
    targetBuyer: "Chief People Officers, Heads of Talent & Leadership, Executive Coaches, Managing Directors",
  },
  learn_smartly: {
    id: "learn_smartly",
    name: "LearnSmartly Institute",
    domain: "learnsmartly.institute",
    badge: "LearnSmartly",
    tagline: "Enterprise capability building & corporate learning academies",
    coreOffering: "High-impact learning architecture, modern capability transformation, and scalable adult learning programs",
    targetBuyer: "Chief Learning Officers (CLOs), VPs of L&D, Organizational Development Directors, HR Executives",
  },
  ennea_world: {
    id: "ennea_world",
    name: "Ennea World",
    domain: "ennea.world",
    badge: "Ennea World",
    tagline: "Global ecosystem, executive advisory & organizational alignment",
    coreOffering: "Cross-functional leadership alignment, executive scaling frameworks, and international strategic partnership networks",
    targetBuyer: "CEOs, Managing Directors, Founders, Group Board Executives",
  },
};

export const GROUP_ENTITY_CHOICES = [
  { id: "five_lens", name: "🌟 Five Lens Enneagram (fivelensenneagram.com)" },
  { id: "learn_smartly", name: "🎓 LearnSmartly Institute (learnsmartly.institute)" },
  { id: "ennea_world", name: "🌐 Ennea World (ennea.world)" },
];
